export type RemoteSeiReceivedPayload = {
  sei: string
  userId: string
}

export type SeiGateState = {
  expectedSei: string | null
  sei: string | null
  userId: string | null
  matched: boolean
}

export type RtcSessionSeiOptions = {
  repeatCount?: number
}

type RtcSeiSenderOptions = {
  canSend: () => boolean
  send: (message: string, repeatCount: number) => unknown
  onError: (error: unknown) => void
}

/** Refresh repeated SEI coverage at a moderate cadence. */
const SESSION_SEI_INTERVAL_MS = 200
const DEFAULT_SEI_REPEAT_COUNT = 4
/** Matches @volcengine/rtc SEI_TIME_OUT; keep the console filter until in-flight timeouts settle. */
const SEI_TIMEOUT_MS = 2000
const SEI_TIMEOUT_CONSOLE_PATTERN = /timeout for sei message/i

let seiTimeoutConsoleFilterDepth = 0
let originalConsoleError: ((...args: unknown[]) => void) | null = null

/**
 * Mute Volcengine's hard-coded `console.error` for SEI send timeouts.
 * `setLogConfig` does not cover this path — the SDK logs it directly.
 */
function acquireSeiTimeoutConsoleFilter(): () => void {
  if (seiTimeoutConsoleFilterDepth === 0) {
    originalConsoleError = console.error.bind(console)
    console.error = (...args: unknown[]) => {
      const first = args[0]
      if (typeof first === 'string' && SEI_TIMEOUT_CONSOLE_PATTERN.test(first)) {
        return
      }
      originalConsoleError?.(...args)
    }
  }
  seiTimeoutConsoleFilterDepth += 1

  let released = false
  return () => {
    if (released) {
      return
    }
    released = true
    seiTimeoutConsoleFilterDepth = Math.max(0, seiTimeoutConsoleFilterDepth - 1)
    if (seiTimeoutConsoleFilterDepth === 0 && originalConsoleError) {
      console.error = originalConsoleError as typeof console.error
      originalConsoleError = null
    }
  }
}

/** Normalize task ids so local and remote SEI values use the same representation. */
export function normalizeSei(value: string | null | undefined): string | null {
  const normalized = value?.trim()
  if (!normalized) {
    return null
  }
  return normalized.startsWith('task-') ? normalized.slice('task-'.length) : normalized
}

/** Decode an RTC SEI payload without allowing malformed data to break event handling. */
export function decodeSeiMessage(sei: Uint8Array): string | null {
  try {
    return new TextDecoder().decode(sei)
  } catch {
    return null
  }
}

/** Keep local task SEI sending separate from RTC room and rendering responsibilities. */
export class RtcSeiSender {
  private message: string | null = null
  private repeatCount = DEFAULT_SEI_REPEAT_COUNT
  private timer: ReturnType<typeof setInterval> | null = null
  private hasReportedError = false
  private sendEpoch = 0
  private releaseConsoleFilter: (() => void) | null = null
  private releaseConsoleFilterTimer: ReturnType<typeof setTimeout> | null = null

  /** Create a sender backed by the owning RTC manager's readiness and send operations. */
  constructor(private readonly options: RtcSeiSenderOptions) {}

  /** Replace the active task message and stop any timer that belonged to the previous task. */
  configure(message: string | null, options: RtcSessionSeiOptions, frameRate: number): void {
    this.stop()
    this.message = message
    this.repeatCount = resolveRepeatCount(options.repeatCount, frameRate)
    this.hasReportedError = false
  }

  /** Whether a task message is currently configured for sending. */
  get configured(): boolean {
    return this.message !== null
  }

  /** Start sending immediately and continue at the configured cadence. */
  start(): void {
    this.stop()
    if (!this.message) {
      return
    }

    this.holdSeiTimeoutConsoleFilter()
    this.timer = setInterval(() => this.sendIfReady(), SESSION_SEI_INTERVAL_MS)
    this.sendIfReady()
  }

  /** Stop future sends without changing the configured task message. */
  stop(): void {
    this.sendEpoch++
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
    this.scheduleReleaseSeiTimeoutConsoleFilter()
  }

  /** Send only while ready; handle both thrown errors and returned promise rejections. */
  private sendIfReady(): void {
    if (!this.message || !this.options.canSend()) {
      return
    }

    const epoch = this.sendEpoch
    const failed = (error: unknown) => {
      if (epoch !== this.sendEpoch) return
      if (!this.hasReportedError) {
        this.hasReportedError = true
        this.options.onError(error)
      }
      this.stop()
    }
    try {
      const result = this.options.send(this.message, this.repeatCount)
      if (result && typeof (result as PromiseLike<unknown>).then === 'function') {
        void Promise.resolve(result).catch(failed)
      }
    } catch (error) { failed(error) }
  }

  private holdSeiTimeoutConsoleFilter(): void {
    if (this.releaseConsoleFilterTimer !== null) {
      clearTimeout(this.releaseConsoleFilterTimer)
      this.releaseConsoleFilterTimer = null
    }
    if (!this.releaseConsoleFilter) {
      this.releaseConsoleFilter = acquireSeiTimeoutConsoleFilter()
    }
  }

  private scheduleReleaseSeiTimeoutConsoleFilter(): void {
    if (!this.releaseConsoleFilter || this.releaseConsoleFilterTimer !== null) {
      return
    }
    this.releaseConsoleFilterTimer = setTimeout(() => {
      this.releaseConsoleFilterTimer = null
      this.releaseConsoleFilter?.()
      this.releaseConsoleFilter = null
    }, SEI_TIMEOUT_MS)
  }
}

/** Clamp repeatCount to the range accepted by the RTC SDK for the configured frame rate. */
function resolveRepeatCount(value: number | undefined, frameRate: number): number {
  const maxRepeatCount = Math.min(30, Math.max(0, Math.floor(frameRate * 2)))
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return Math.min(DEFAULT_SEI_REPEAT_COUNT, maxRepeatCount)
  }
  return Math.max(0, Math.min(maxRepeatCount, Math.floor(value)))
}
